import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.5; transform: scale(0.7); }
`;

const subtitleFade = keyframes`
  from { opacity: 0; transform: translateY(3px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Card = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  padding: 1rem 1.25rem;
  background: rgba(0, 0, 0, 0.02);
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0.875rem;
  gap: 8px;
`;

const TitleGroup = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TitleIcon = styled.svg`
  flex-shrink: 0;
  color: #1a1a1a;
`;

const Subtitle = styled.div`
  font-size: 11.5px;
  color: ${({ $resolving }) => ($resolving ? '#1a1a1a' : '#999')};
  margin-top: 3px;
  animation: ${subtitleFade} 0.25s ease forwards;
  transition: color 0.2s ease;
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 99px;
  background: ${({ $complete }) => ($complete ? '#EAF3DE' : '#ececec')};
  color: ${({ $complete }) => ($complete ? '#3B6D11' : '#1a1a1a')};
`;

const StatusDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${({ $complete }) => ($complete ? '#3B6D11' : '#1a1a1a')};
  animation: ${({ $complete }) => ($complete ? 'none' : pulse)} 1.2s ease-in-out infinite;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #999;
  padding: 0;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #f0f0f0;
    color: #333;
  }

  svg { display: block; }
`;

const TrackBar = styled.div`
  height: 3px;
  background: #f0f0f0;
  border-radius: 99px;
  overflow: hidden;
  margin-bottom: 0.875rem;
`;

const TrackFill = styled.div`
  height: 100%;
  border-radius: 99px;
  background: #1a1a1a;
  width: ${({ $pct }) => $pct}%;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
`;

const StepsRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 10px;
    left: calc(50% + 11px);
    right: calc(-50% + 11px);
    height: 1px;
    background: ${({ $done }) => ($done ? '#1a1a1a' : '#e8e8e8')};
    transition: background 0.4s;
  }
`;

const StepDot = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid ${({ $done, $active }) => ($done || $active ? '#1a1a1a' : '#e0e0e0')};
  background: ${({ $done }) => ($done ? '#1a1a1a' : 'white')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: ${({ $done }) => ($done ? 'white' : '#999')};
  margin-bottom: 5px;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  transition: all 0.3s;

  ${({ $active }) =>
    $active &&
    css`
      &::after {
        content: '';
        position: absolute;
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #1a1a1a;
        animation: ${pulse} 1.2s ease-in-out infinite;
      }
    `}
`;

const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StepLabel = styled.div`
  font-size: 10px;
  color: ${({ $active, $done }) => ($active ? '#1a1a1a' : $done ? '#666' : '#bbb')};
  text-align: center;
  line-height: 1.3;
  white-space: pre-line;
  font-weight: ${({ $active }) => ($active ? '500' : '400')};
  transition: color 0.3s;
`;

const CONVERTING_STEP = { label: 'Converting\ndocument', subtitle: 'Converting PDF to searchable text' };
const CHUNKING_STEP = { label: 'Chunking &\nclassifying', subtitle: 'Splitting into chunks · running AECO classifier' };
const EXTRACTING_STEP = {
  label: 'Extracting\nrequirements',
  subtitle: 'Reading document structure and prose requirements',
  subtitleResolving: 'Resolving cross-references — resuming extraction shortly…',
};
const EXTRACTING_TABLES_STEP = {
  label: 'Extracting\ntables',
  subtitle: 'Parsing table data and building row-by-row requirements',
};
const DRAWING_LABELS_STEP = { label: 'Finding labels\non drawings', subtitle: 'Detecting components and labels on drawing sheets' };
const COMPLETE_STEP = { label: 'Complete', subtitle: 'Documents indexed and searchable in Research' };

const FILL_PCTS_BY_LENGTH = {
  5: [8, 28, 55, 82, 100],
  4: [10, 35, 70, 100],
  3: [15, 55, 100],
};

/**
 * Builds the visible step list for a batch. Requirement docs go through a real
 * extraction + page-mapping pass; pure deliverables don't, so those steps would
 * never be relevant to show. Drawing deliverables get their own "finding labels"
 * step instead.
 */
function buildSteps({ isDeliverableOnly, hasDrawingDeliverable }) {
  const steps = [CONVERTING_STEP, CHUNKING_STEP];
  if (isDeliverableOnly) {
    if (hasDrawingDeliverable) steps.push(DRAWING_LABELS_STEP);
  } else {
    steps.push(EXTRACTING_STEP, EXTRACTING_TABLES_STEP);
  }
  steps.push(COMPLETE_STEP);
  return steps;
}

/**
 * The backend only tracks one indexing pipeline (0–4, see mapChunkingStageToStep
 * below) regardless of document mix, so when the visible step list is shorter
 * we collapse the backend's middle stages onto whichever visible step is last
 * before "Complete".
 */
function remapStepIndex(fullStepIndex, stepsLength) {
  if (fullStepIndex >= 4) return stepsLength - 1;
  return Math.min(fullStepIndex, stepsLength - 2);
}

/** Map chunking-jobs API stage to the full 0–4 pipeline step index. */
export function mapChunkingStageToStep(stage, status) {
  const s = String(stage || 'WAITING').toUpperCase();
  if (status === 'COMPLETE' || s === 'COMPLETE') return 4;
  if (s === 'INDEXING' || s === 'REFRESHING') return 3;
  if (s === 'EMBEDDING') return 2;
  if (s === 'CHUNKING') return 1;
  return 0;
}

const ExtractionProgressBar = ({
  documentName = 'Document',
  stepIndex = 0,
  isComplete = false,
  isResolving = false,
  liveSubtitle,
  isDeliverableOnly = false,
  hasDrawingDeliverable = false,
}) => {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isComplete) setDismissed(false);
  }, [isComplete, documentName]);

  if (dismissed) return null;

  const STEPS = buildSteps({ isDeliverableOnly, hasDrawingDeliverable });
  const FILL_PCTS = FILL_PCTS_BY_LENGTH[STEPS.length];
  const activeStep = Math.min(Math.max(remapStepIndex(stepIndex, STEPS.length), 0), STEPS.length - 1);
  const pct = FILL_PCTS[activeStep];
  const currentStep = STEPS[activeStep];

  const showResolvingSubtitle = !!currentStep.subtitleResolving && isResolving && !isComplete;
  const subtitle = liveSubtitle || (showResolvingSubtitle
    ? currentStep.subtitleResolving
    : currentStep.subtitle);

  return (
    <Card role="status" aria-label="Extraction progress">
      <Header>
        <TitleGroup>
          <Title>
            <TitleIcon
              width="14" height="14" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              viewBox="0 0 24 24" aria-hidden="true"
            >
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </TitleIcon>
            {documentName}
          </Title>
          <Subtitle key={subtitle} $resolving={showResolvingSubtitle}>{subtitle}</Subtitle>
        </TitleGroup>

        <RightGroup>
          <StatusBadge $complete={isComplete}>
            <StatusDot $complete={isComplete} />
            {isComplete ? 'Complete' : 'Processing'}
          </StatusBadge>

          {isComplete && (
            <CloseBtn onClick={() => setDismissed(true)} aria-label="Dismiss extraction progress">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </CloseBtn>
          )}
        </RightGroup>
      </Header>

      <TrackBar>
        <TrackFill $pct={pct} />
      </TrackBar>

      <StepsRow role="list">
        {STEPS.map((step, i) => {
          const done = i < activeStep;
          const active = i === activeStep;
          return (
            <StepItem key={i} $done={done} role="listitem">
              <StepDot $done={done} $active={active && !isComplete}>
                {done || (active && isComplete) ? <CheckIcon /> : i + 1}
              </StepDot>
              <StepLabel $active={active && !isComplete} $done={done || (active && isComplete)}>
                {step.label}
              </StepLabel>
            </StepItem>
          );
        })}
      </StepsRow>
    </Card>
  );
};

export default ExtractionProgressBar;
